/**
 * Copyright (c) 2025 Aisle 24. All rights reserved.
 *
 * Proprietary Software - Licensed for Internal Use Only
 *
 * This software and associated documentation files (the "Software") are proprietary
 * to Aisle 24 and are protected by copyright laws and international copyright
 * treaties, as well as other intellectual property laws and treaties.
 *
 * The Software is licensed, not sold, to you for use only under the terms of this
 * license agreement. All rights not expressly granted to you are reserved by Aisle 24.
 *
 * Unauthorized copying, modification, distribution, or use of this Software,
 * via any medium is strictly prohibited and may result in severe civil and
 * criminal penalties, and will be prosecuted to the maximum extent possible
 * under the law.
 */

// Application Constants
import { CURRENT_BRANDING } from '../config/brandingConfig';

export const APP_CONFIG = {
  NAME: CURRENT_BRANDING.displayName,
  VERSION: '0.0.58',
  BUILD_NUMBER: 58,
  DESCRIPTION: CURRENT_BRANDING.description,
  AUTHOR: CURRENT_BRANDING.name,
  COPYRIGHT: CURRENT_BRANDING.copyright,
  POS_NUMBER: '#A24-POS-212', // Unique POS identifier
  TESTING_MODE: true, // Bypass payment validation for testing
  LOCATION_ID: 7,  // Default location ID for the bookstore
};

// Branding (from config)
export const BRANDING = {
  STORE_NAME: CURRENT_BRANDING.store.name,
  BOOKSTORE_NAME: CURRENT_BRANDING.store.name,
  ADDRESS: CURRENT_BRANDING.store.address || {
    STREET: '',
    CITY: '',
    PROVINCE: '',
    POSTAL_CODE: 'L1H 7K4',
    PHONE: '(905) 721-8668',
  },
  COLORS: {
    PRIMARY: '#2C507A',
    SECONDARY: '#F06C38',
    SUCCESS: '#F06C38',
    ERROR: '#DC3545',
    WARNING: '#FFC107',
    INFO: '#2C507A',
  },
};

// UI Text and Messages
export const UI_TEXT = {
  // Header
  HEADER: {
    TITLE: 'Convenient Self-Checkout',
    SUBTITLE: 'Cashier-less POS',
  },
  
  // Scanning Interface
  SCANNING: {
    TITLE: 'Scan Your Items',
    SUBTITLE: 'Enter barcode manually',
    READY_TO_SCAN: 'Ready to Scan',
    SCAN_INSTRUCTION: 'Click the scan button to start scanning barcodes',
    START_SCANNING: 'Start Scanning',
    MANUAL_ENTRY: 'Manual Entry',
    VIEW_CART: 'View Cart',
    PRODUCT_FOUND: 'Product Found!',
    SCAN_AGAIN: 'Scan Again',
    ADD_TO_CART: 'Add to Cart',
    MANUAL_BARCODE_TITLE: 'Manual Barcode Entry',
    MANUAL_BARCODE_INSTRUCTION: 'Enter the barcode number manually:',
    MANUAL_BARCODE_PLACEHOLDER: 'Enter barcode...',
    FIND_PRODUCT: 'Find Product',
    CANCEL: 'Cancel',
  },
  
  // Cart Interface
  CART: {
    TITLE: 'Shopping Cart',
    EMPTY_TITLE: 'Your cart is empty',
    EMPTY_INSTRUCTION: 'Start scanning items to add them to your cart',
    CONTINUE_SHOPPING: 'Continue Shopping',
    PROCEED_TO_PAYMENT: 'Proceed to Payment',
    ORDER_SUMMARY: 'Order Summary',
    ITEMS_COUNT: 'items',
    TOTAL_LABEL: 'Total (including tax)',
  },
  
  // Payment Interface
  PAYMENT: {
    TITLE: 'Payment',
    BACK_TO_CART: 'Back to Cart',
    SELECT_METHOD: 'Select Payment Method',
    PAYMENT_DETAILS: 'Payment Details',
    ORDER_SUMMARY: 'Order Summary',
    SUBTOTAL: 'Subtotal:',
    TAX_LABEL: 'Tax (HST):',
    TOTAL: 'Total:',
    PROCESSING: 'Processing Payment...',
    PAY_BUTTON: 'Pay',
    PAYMENT_METHODS: {
      CARD: 'Credit Card',
      DEBIT: 'Debit Card',
      STUDENT_CARD: 'Student Card',
      GIFT_CARD: 'Gift Card',
      CASH: 'Cash',
    },
    FORM_LABELS: {
      CARD_NUMBER: 'Card Number',
      EXPIRY_DATE: 'Expiry Date',
      CVV: 'CVV',
      PIN: 'PIN',
      STUDENT_ID: 'Student ID',
      GIFT_CARD_NUMBER: 'Gift Card Number',
      GIFT_CARD_PIN: 'PIN',
    },
    PLACEHOLDERS: {
      CARD_NUMBER: '1234 5678 9012 3456',
      EXPIRY_DATE: 'MM/YY',
      CVV: '123',
      STUDENT_ID: 'Enter your student ID',
      GIFT_CARD_NUMBER: 'Enter gift card number',
      GIFT_CARD_PIN: 'Enter PIN',
    },
    MESSAGES: {
      STUDENT_ACCOUNT: 'Payment will be charged to your student account. Please ensure sufficient funds are available.',
      CASH_INSTRUCTION: 'Please insert cash into the payment slot. The system will calculate change automatically.',
      PAYMENT_FAILED: 'Payment failed. Please try again or use a different payment method.',
      PAYMENT_ERROR: 'An error occurred during payment processing.',
    },
  },
  
  // Receipt Interface
  RECEIPT: {
    SUCCESS_TITLE: 'Payment Successful!',
    SUCCESS_MESSAGE: 'Thank you for your purchase',
    RECEIPT_TITLE: CURRENT_BRANDING.receipt.title,
    ITEMS_PURCHASED: 'Items Purchased',
    RECEIPT_NUMBER: 'Receipt #:',
    DATE: 'Date:',
    CASHIER: 'Cashier:',
    CASHIER_NAME: 'Self-Service Kiosk',
    SUBTOTAL: 'Subtotal:',
    TAX: 'Tax (HST):',
    TOTAL: 'Total:',
    FOOTER_MESSAGE: CURRENT_BRANDING.receipt.footerMessage,
    KEEP_RECEIPT: 'Keep this receipt for your records',
    PRINT: 'Print',
    EMAIL: 'Email',
    DOWNLOAD: 'Download',
    NEW_TRANSACTION: 'New Transaction',
  },
  
  // Common
  COMMON: {
    LOADING: 'Loading...',
    ERROR: 'Error',
    SUCCESS: 'Success',
    WARNING: 'Warning',
    INFO: 'Info',
    CONFIRM: 'Confirm',
    CANCEL: 'Cancel',
    SAVE: 'Save',
    EDIT: 'Edit',
    DELETE: 'Delete',
    ADD: 'Add',
    REMOVE: 'Remove',
    CLOSE: 'Close',
    BACK: 'Back',
    NEXT: 'Next',
    PREVIOUS: 'Previous',
    FINISH: 'Finish',
    CONTINUE: 'Continue',
    RETRY: 'Retry',
    REFRESH: 'Refresh',
  },
};

// Error Messages
export const ERROR_MESSAGES = {
  PRODUCT_NOT_FOUND: 'Product with barcode {barcode} not found.',
  PAYMENT_FAILED: 'Payment failed. Please try again or use a different payment method.',
  PAYMENT_ERROR: 'An error occurred during payment processing.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNKNOWN_ERROR: 'An unknown error occurred.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  PRODUCT_ADDED: 'Product added to cart successfully.',
  PAYMENT_SUCCESS: 'Payment processed successfully.',
  CART_CLEARED: 'Cart cleared successfully.',
  RECEIPT_GENERATED: 'Receipt generated successfully.',
};

// Sample Barcodes for Testing
export const SAMPLE_BARCODES = {
  ALGORITHMS: '9780262033848',
  CALCULUS: '9781337552516',
  PHYSICS: '9780133942651',
  CHEMISTRY: '9781260475678',
  LINEAR_ALGEBRA: '9780135851258',
  JAVA_DS: '9781118771334',
  MICROBIOLOGY: '9780134605180',
  STATICS: '9780133918922',
  HOODIE: 'OTU001',
  TSHIRT: 'OTU002',
  CALCULATOR: 'CALC001',
  GRAPH_PAPER: 'PAPER001',
};

// Payment Configuration
export const PAYMENT_CONFIG = {
  TAX_RATE: 0.1, // 10% HST
  SUCCESS_RATE: 0.95, // 95% success rate
  PROCESSING_TIME: 3000, // 3 seconds
  CURRENCY: 'CAD',
  CURRENCY_SYMBOL: '$',
};


// ISBN/Barcode Configuration
export const BARCODE_CONFIG = {
  SUPPORTED_FORMATS: [
    'EAN_13',    // ISBN-13
    'EAN_8',     // ISBN-8
    'UPC_A',     // UPC-A
    'UPC_E',     // UPC-E
    'CODE_128',  // Code 128
    'CODE_39',   // Code 39
    'CODE_93',   // Code 93
  ],
  AUTO_ADD_DELAY: 2000, // 2 seconds
  ERROR_DISPLAY_TIME: 3000, // 3 seconds
  CLEAN_PATTERN: /\D/g, // Remove non-digits
};

// Kiosk Configuration
export const KIOSK_CONFIG = {
  FULLSCREEN_DELAY: 1000, // 1 second
  ORIENTATION: 'portrait',
  TOUCH_TARGET_SIZE: 48, // 48px minimum
  AUTO_REFRESH_INTERVAL: 30000, // 30 seconds
};

// API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'https://api.ontariotechbookstore.com',
  TIMEOUT: 10000, // 10 seconds
  RETRY_ATTEMPTS: 3,
  ENDPOINTS: {
    PRODUCTS: '/api/products',
    INVENTORY: '/api/inventory',
    PAYMENTS: '/api/payments',
    RECEIPTS: '/api/receipts',
    STUDENTS: '/api/students',
    TRANSACTIONS: '/api/transactions',
  },
};

// Local Storage Keys
export const STORAGE_KEYS = {
  CART: 'ontario_tech_cart',
  USER_PREFERENCES: 'ontario_tech_preferences',
  RECENT_TRANSACTIONS: 'ontario_tech_recent_transactions',
  THEME: 'ontario_tech_theme',
};

// Feature Flags
export const FEATURES = {
  MANUAL_ENTRY: true,
  MULTIPLE_PAYMENT_METHODS: true,
  RECEIPT_EMAIL: true,
  RECEIPT_DOWNLOAD: true,
  STUDENT_DISCOUNTS: true,
  GIFT_CARDS: true,
  OFFLINE_MODE: true,
};

// Validation Rules
export const VALIDATION = {
  BARCODE: {
    MIN_LENGTH: 8,
    MAX_LENGTH: 20,
    PATTERN: /^[0-9A-Za-z]+$/,
  },
  CARD_NUMBER: {
    MIN_LENGTH: 13,
    MAX_LENGTH: 19,
    PATTERN: /^[0-9\s]+$/,
  },
  CVV: {
    MIN_LENGTH: 3,
    MAX_LENGTH: 4,
    PATTERN: /^[0-9]+$/,
  },
  STUDENT_ID: {
    MIN_LENGTH: 6,
    MAX_LENGTH: 12,
    PATTERN: /^[0-9A-Za-z]+$/,
  },
};

// Receipt Configuration
export const RECEIPT_CONFIG = {
  WIDTH: 80, // characters
  FONT_SIZE: 12,
  MARGIN: 2,
  SEPARATOR: '-',
  DOUBLE_SEPARATOR: '=',
};

// Accessibility
export const ACCESSIBILITY = {
  ARIA_LABELS: {
    SCAN_BUTTON: 'Start barcode scanning',
    CART_BUTTON: 'View shopping cart',
    PAYMENT_BUTTON: 'Proceed to payment',
    PRINT_BUTTON: 'Print receipt',
    EMAIL_BUTTON: 'Email receipt',
    DOWNLOAD_BUTTON: 'Download receipt',
  },
  KEYBOARD_SHORTCUTS: {
    SCAN: 'Space',
    CART: 'C',
    PAYMENT: 'P',
    PRINT: 'Ctrl+P',
    ESCAPE: 'Escape',
  },
};